import { AddressSearch } from '@application/components';
import { UserContext } from '@application/contexts';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLookupContext from '../context/SiteLookupContext';
import RadiusFilter from '../filters/RadiusFilter';
import { siteLookupTokens } from '../siteLookupTokens';
import DownloadButtonTooltip from './DownloadButtonTooltip';
import DownloadReportMenu from './DownloadReportMenu';

const SearchBar = () => {
  const { downloadReport, filters, maxHistoricalMonths, pendingRequest, recentSearches, remainingDownloads, result, updateFilterAddress, updateFilterRadius } =
    useContext(SiteLookupContext);
  const { tenant } = useContext(UserContext);
  const { t } = useTranslation();

  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  // The report always uses the displayed (applied) results: nothing to download while they are loading or empty.
  const isDownloadReportDisabled =
    remainingDownloads <= 0 || !result || !!pendingRequest || result.data.stationsCount === 0 || result.data.includedInReportCount === 0;

  // While a search is pending the previous count would describe other criteria: show no number.
  const data = !pendingRequest ? result?.data : undefined;
  const exportedPortsCount = data ? data.includedInReportPortsCount : null;
  const isReportCapped = !!data && data.matchingPortsCount > data.includedInReportPortsCount;

  return (
    <Box display="flex" flexDirection="column" gap={0.75}>
      <Box display="flex" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} alignItems="center" gap={2}>
        <Box flex="1 1 auto" minWidth={{ xs: '100%', sm: 0 }} maxWidth={720}>
          <AddressSearch
            endAdornment={<RadiusFilter embedded onChange={updateFilterRadius} value={filters.radius} />}
            onSelect={updateFilterAddress}
            recentSearches={recentSearches}
            ref={ref}
            value={filters.address?.fullAddress || ''}
          />
        </Box>

        <Box flexGrow={1} display={{ xs: 'none', sm: 'block' }} />

        <DownloadButtonTooltip>
          <Box sx={{ minWidth: 'fit-content' }}>
            <DownloadReportMenu
              exportedPortsCount={exportedPortsCount}
              onDownloadReport={downloadReport}
              isDownloadReportDisabled={isDownloadReportDisabled}
              maxHistoricalMonths={maxHistoricalMonths}
            />
          </Box>
        </DownloadButtonTooltip>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="flex-start" justifyContent="space-between" columnGap={3} rowGap={0.5}>
        <Typography color={siteLookupTokens.color.text.muted} fontSize={12}>
          {t('siteLookup.quota', {
            downloads: t('siteLookup.quotaDownloads', { count: tenant?.remainingDownloads ?? 0 }),
            searches: t('siteLookup.quotaSearches', { count: tenant?.remainingSearches ?? 0 }),
          })}
        </Typography>

        {isReportCapped && (
          <Box display="flex" alignItems="flex-start" gap={0.5} marginLeft="auto" maxWidth={{ md: '60%' }} color={siteLookupTokens.color.warning}>
            <InfoOutlined aria-hidden sx={{ fontSize: 16, marginTop: '1px' }} />
            <Typography color="inherit" fontSize={12} textAlign="right">
              {t('siteLookup.exportCapped', { exported: data.includedInReportPortsCount, count: data.matchingPortsCount })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SearchBar;
